import React, { useRef, useState } from 'react';
import { Alert, Box, CircularProgress, FormControlLabel, Stack, Switch, Typography } from '@mui/material';
import { Description as DescriptionIcon, UploadFile as UploadFileIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

interface SdsDropzoneProps {
  disabled?: boolean;
  loading?: boolean;
  title: string;
  buttonLabel: string;
  helperText: string;
  extractLabelTextSwitchLabel?: string;
  hideExtractToggle?: boolean;
  currentFileName?: string | null;
  error?: string | null;
  warning?: string | null;
  onFileSelected: (file: File, extractLabelTextFromClp: boolean) => void | Promise<void>;
}

export const SdsDropzone: React.FC<SdsDropzoneProps> = ({
  disabled = false,
  loading = false,
  title,
  buttonLabel,
  helperText,
  extractLabelTextSwitchLabel,
  hideExtractToggle = false,
  currentFileName,
  error,
  warning,
  onFileSelected,
}) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const [extractLabelTextFromClp, setExtractLabelTextFromClp] = useState(true);

  const handleFile = (file: File | null | undefined) => {
    if (!file || disabled || loading) return;
    void onFileSelected(file, hideExtractToggle ? false : extractLabelTextFromClp);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    handleFile(event.target.files?.[0]);
    event.target.value = '';
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragActive(false);
    handleFile(event.dataTransfer.files?.[0]);
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (!disabled && !loading) {
      setIsDragActive(true);
    }
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragActive(false);
  };

  return (
    <Stack spacing={1}>
      <Box
        sx={{
          border: '1px solid',
          borderColor: isDragActive ? 'primary.main' : 'divider',
          borderRadius: 1.25,
          overflow: 'hidden',
          transition: 'border-color 0.2s ease',
        }}
      >
        <Stack direction="row" sx={{ minHeight: 96 }}>
          {/* Left: info panel */}
          <Box
            sx={{
              p: 1.75,
              flex: '0 0 44%',
              borderRight: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
            }}
          >
            <Typography variant="subtitle2" fontWeight={600} lineHeight={1.3}>
              {title}
            </Typography>
            {currentFileName ? (
              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ overflow: 'hidden' }}>
                <DescriptionIcon sx={{ fontSize: 13, color: 'primary.main', flexShrink: 0 }} />
                <Typography
                  variant="caption"
                  color="primary.main"
                  fontWeight={500}
                  sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                  {currentFileName}
                </Typography>
              </Stack>
            ) : (
              <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4 }}>
                {helperText}
              </Typography>
            )}
            {!hideExtractToggle && (
              <FormControlLabel
                sx={{
                  m: 0,
                  mt: 'auto',
                  pt: 0.5,
                  '& .MuiFormControlLabel-label': {
                    fontSize: '0.72rem',
                    lineHeight: 1.3,
                    color: 'text.secondary',
                  },
                }}
                control={
                  <Switch
                    size="small"
                    checked={extractLabelTextFromClp}
                    onChange={(event) => setExtractLabelTextFromClp(event.target.checked)}
                    disabled={disabled || loading}
                    sx={{ mr: 0.5 }}
                  />
                }
                label={extractLabelTextSwitchLabel || t('products.sdsExtractLabelTextSwitch')}
              />
            )}
          </Box>

          {/* Right: drop zone */}
          <Box
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={!disabled && !loading ? () => inputRef.current?.click() : undefined}
            sx={{
              flex: 1,
              p: 1.75,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.5,
              cursor: disabled || loading ? 'default' : 'pointer',
              backgroundColor: isDragActive ? 'action.hover' : 'transparent',
              transition: 'background-color 0.2s ease',
              '&:hover': {
                backgroundColor: disabled || loading ? undefined : 'action.hover',
              },
            }}
          >
            <input
              ref={inputRef}
              type="file"
              accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              hidden
              onChange={handleInputChange}
            />
            {loading ? (
              <>
                <CircularProgress size={22} />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                  {t('products.sdsUploading')}
                </Typography>
              </>
            ) : (
              <>
                <UploadFileIcon
                  sx={{
                    fontSize: 24,
                    color: isDragActive ? 'primary.main' : 'text.disabled',
                    transition: 'color 0.2s ease',
                  }}
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  align="center"
                  sx={{ lineHeight: 1.3, maxWidth: 140 }}
                >
                  {t('products.sdsDropHint')}
                </Typography>
                <Typography
                  variant="caption"
                  color="primary.main"
                  fontWeight={600}
                  sx={{ fontSize: '0.72rem', mt: 0.25 }}
                >
                  {buttonLabel}
                </Typography>
              </>
            )}
          </Box>
        </Stack>
      </Box>
      {warning && (
        <Alert severity="warning" sx={{ py: 0.5, '& .MuiAlert-message': { fontSize: '0.8rem', py: 0.5 } }}>
          {warning}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ py: 0.5, '& .MuiAlert-message': { fontSize: '0.8rem', py: 0.5 } }}>
          {error}
        </Alert>
      )}
    </Stack>
  );
};
